import { z } from 'zod';

const NameField = z
  .string()
  .min(1)
  .max(100)
  .transform((s) => s.trim())
  .refine((s) => s.length > 0, { message: 'name must contain non-whitespace characters' });

/**
 * @aiPurpose validate + parse a Department entity from API response or DB row.
 * @aiInvariant `name` is trimmed (no leading/trailing whitespace) and 1..100
 *   characters; `archivedAt` is `null` when active, ISO-8601 timestamp when
 *   retired; uniqueness is case-insensitive across both states (enforced by
 *   `lower(name)` Postgres functional index, not by this schema).
 */
export const DepartmentSchema = z
  .object({
    id: z.string().min(1),
    name: NameField,
    archivedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict();

/**
 * @aiPurpose compact Department projection embedded in user/session payloads
 *   that need a human-readable department label without exposing archive or
 *   audit timestamps.
 * @aiInvariant `id` matches `User.departmentId`; `name` uses the same trimmed
 *   display-name rules as `DepartmentSchema`.
 */
export const DepartmentRefSchema = DepartmentSchema.pick({
  id: true,
  name: true,
});
export type DepartmentRef = z.infer<typeof DepartmentRefSchema>;

/**
 * @aiPurpose Department type inferred from {@link DepartmentSchema}.
 * @aiInvariant matches `DepartmentSchema` exactly — never declared independently.
 */
export type Department = z.infer<typeof DepartmentSchema>;

/**
 * @aiPurpose POST /api/departments body shape.
 * @aiInvariant `.strict()` rejects unknown keys; `name` is trimmed by transform
 *   before persistence so server-side uniqueness on `lower(trim(name))` matches
 *   client-perceived uniqueness.
 */
export const CreateDepartmentInputSchema = z
  .object({
    name: NameField,
  })
  .strict();
export type CreateDepartmentInput = z.infer<typeof CreateDepartmentInputSchema>;

/**
 * @aiPurpose PATCH /api/departments/:id body shape (rename only).
 * @aiInvariant rename is the only mutation this endpoint accepts; archive/
 *   unarchive flows through PATCH /api/departments/:id/archive.
 */
export const PatchDepartmentInputSchema = z
  .object({
    name: NameField,
  })
  .strict();
export type PatchDepartmentInput = z.infer<typeof PatchDepartmentInputSchema>;

/**
 * @aiPurpose PATCH /api/departments/:id/archive body shape (archive toggle).
 * @aiInvariant `archived: true` flips `archivedAt` to current server time;
 *   `archived: false` flips it back to `null`. The transition is idempotent
 *   on the server (re-archiving an already-archived department is a no-op).
 */
export const ArchiveDepartmentInputSchema = z
  .object({
    archived: z.boolean(),
  })
  .strict();
export type ArchiveDepartmentInput = z.infer<typeof ArchiveDepartmentInputSchema>;

/**
 * @aiPurpose GET /api/departments query-string filter.
 * @aiInvariant accepts the boolean primitive AND the string forms `'true'` /
 *   `'false'` so the same schema parses both fetch-client encoded queries and
 *   raw URL strings; absent `active` returns all departments (active+archived).
 */
export const DepartmentListFilterSchema = z
  .object({
    active: z
      .union([z.boolean(), z.enum(['true', 'false'])])
      .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
      .optional(),
  })
  .strict();
export type DepartmentListFilter = z.infer<typeof DepartmentListFilterSchema>;
